import React from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { getDatePart, formatDate, addTime } from '../../../utils/date.js';

const TimeSeries = ({ data, options }) => {
  if (!data || !data.labels || !data.datasets) {
    return (
      <div className="w-full h-[300px] flex items-center justify-center text-gray-500">
        No chart data available
      </div>
    );
  }

  // For month-based data, we don't need to fill in missing dates
  // as we want to show the actual data points
  const isMonthData = data.labels.some(label => /^\d{4}-\d{2}$/.test(label));

  // Ensure all Y values are properly parsed as numbers
  const ensureNumericValues = (inputData) => {
    return {
      ...inputData,
      datasets: inputData.datasets.map(dataset => ({
        ...dataset,
        data: dataset.data.map(value =>
          typeof value === 'string' ? parseFloat(value) || 0 : value
        )
      }))
    };
  };

  // Format the data for time series
  const formatTimeSeriesData = (inputData) => {
    try {
      // First ensure all values are numeric
      const numericData = ensureNumericValues(inputData);

      // If it's month data, just return the processed data
      if (isMonthData) {
        return {
          ...numericData,
          datasets: numericData.datasets.map(dataset => ({
            ...dataset,
            pointRadius: 5,
            pointHoverRadius: 8
          }))
        };
      }

      // For daily data, proceed with filling in missing dates
      // Get all dates from the data
      const originalDates = numericData.labels;
      if (!originalDates.length) return numericData;

      // Extract just the date parts from timestamps using our utility
      const dateParts = originalDates.map(getDatePart);

      // Create a date range from min to max date
      const minDate = new Date(Math.min(...dateParts.map(d => new Date(d))));
      const maxDate = new Date(Math.max(...dateParts.map(d => new Date(d))));

      // Validate dates
      if (isNaN(minDate.getTime()) || isNaN(maxDate.getTime())) return numericData;

      // Create an array of all dates in the range
      const allDates = [];
      let currentDate = new Date(minDate);

      while (currentDate <= maxDate) {
        allDates.push(formatDate(currentDate, 'YYYY-MM-DD'));
        currentDate = addTime(currentDate, 1, 'day');
      }

      // Group the data points by day, using the last value for each day
      const dataMap = {};
      originalDates.forEach((fullDate, index) => {
        const datePart = getDatePart(fullDate);
        // If multiple readings exist for the same day, use the latest value
        dataMap[datePart] = numericData.datasets[0].data[index];
      });

      // Create the final dataset with null values for missing dates (to show gaps)
      const formattedData = {
        labels: allDates,
        datasets: [{
          ...numericData.datasets[0],
          data: allDates.map(day => dataMap[day] !== undefined ? dataMap[day] : null),
          spanGaps: true // Show gaps for missing data points
        }]
      };

      return formattedData;
    } catch (error) {
      console.error('Error formatting time series data:', error);
      return inputData;
    }
  };

  const formattedData = formatTimeSeriesData(data);

  // Transform Chart.js data format to Recharts format
  // Chart.js format: { labels: [...], datasets: [{label, data, ...}] }
  // Recharts format: [{ name: 'date', value: number }, ...]

  const transformData = () => {
    if (!formattedData || !formattedData.labels || !formattedData.datasets) {
      return [];
    }

    return formattedData.labels.map((label, index) => {
      const dataPoint = { name: label };
      formattedData.datasets.forEach((dataset) => {
        const key = dataset.label || 'value';
        dataPoint[key] = dataset.data[index];
      });
      return dataPoint;
    });
  };

  const chartData = transformData();

  // Extract dataset keys for rendering lines
  const dataKeys = formattedData?.datasets?.map(ds => ds.label || 'value') || ['value'];

  // Define color palette with gray tones
  const colors = ['#374151', '#6b7280', '#9ca3af', '#d1d5db'];

  // Find the max Y value to set an appropriate scale
  const maxValue = Math.max(...chartData.map(d => {
    const values = dataKeys.map(key => d[key]).filter(v => v !== null && v !== undefined);
    return Math.max(...values);
  }).filter(v => !isNaN(v)));

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border border-gray-300 rounded shadow-sm">
          <p className="text-sm font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value !== null ? entry.value.toLocaleString() : 'N/A'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Format Y-axis tick
  const formatYAxis = (value) => {
    if (value >= 1000) {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    return value;
  };

  return (
    <div className="w-full h-[300px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            fontSize={12}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis
            fontSize={12}
            tickFormatter={formatYAxis}
            domain={[0, maxValue * 1.1]}
          />
          <Tooltip content={<CustomTooltip />} />
          {dataKeys.length > 1 && <Legend />}
          {dataKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={colors[index % colors.length]}
              strokeWidth={2}
              dot={{ r: isMonthData ? 5 : 3 }}
              activeDot={{ r: isMonthData ? 8 : 5 }}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

TimeSeries.propTypes = {
  data: PropTypes.object.isRequired,
  options: PropTypes.object
};

export default TimeSeries;
